import React, { useEffect, useRef } from 'react'
import { observer, useLocalObservable } from 'mobx-react-lite'
import { runInAction } from 'mobx'
import axiosService from '../utils/AxiosService'
import { useParams } from 'react-router-dom'
import Action from '../models/Action'
import CampaignScenariosProjectAsset from '../models/CampaignScenariosProjectAsset'
import { toastError } from '../components/FormComponents/Toasts'
import { differenceInCalendarDays, endOfWeek, format, startOfWeek } from 'date-fns'

export const TimelineContext = React.createContext({})

export const TIMELINE_DATE_COLUMN_WIDTH = 50
export const TIMELINE_WEEK_STARTS_ON = 1

export const normalizeTimelineDate = (value) => {
  if (!value) return null

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null
    return new Date(value.getFullYear(), value.getMonth(), value.getDate())
  }

  if (typeof value === 'string') {
    const dateOnlyMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/)
    if (dateOnlyMatch) {
      const [, year, month, day] = dateOnlyMatch
      return new Date(Number(year), Number(month) - 1, Number(day))
    }
  }

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return null
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate())
}

export const getTimelineDateKey = (value) => {
  const date = normalizeTimelineDate(value)
  return date ? format(date, 'yyyy-MM-dd') : null
}

export const isTimelineToday = (value) => {
  const date = normalizeTimelineDate(value)
  const today = normalizeTimelineDate(new Date())
  return Boolean(date && today && date.getTime() === today.getTime())
}

export const isTimelineMonday = (value) => normalizeTimelineDate(value)?.getDay() === 1

export const isTimelineWeekend = (value) => {
  const day = normalizeTimelineDate(value)?.getDay()
  return day === 0 || day === 6
}

export const getTimelineWeekStart = (value) => startOfWeek(normalizeTimelineDate(value) || new Date(), {
  weekStartsOn: TIMELINE_WEEK_STARTS_ON,
})

export const getTimelineWeekEnd = (value) => endOfWeek(normalizeTimelineDate(value) || new Date(), {
  weekStartsOn: TIMELINE_WEEK_STARTS_ON,
})

export default observer(function TimelineProvider({
  children,
  campaignScenario,
  readonly = false,
  layout = 'page',
}) {
  const { id } = useParams()
  const campaignIdRef = useRef(id)
  const scenarioIdRef = useRef(null)
  useEffect(() => {
    campaignIdRef.current = id
  }, [id])
  scenarioIdRef.current = campaignScenario?.id

  const timelineState = useLocalObservable(() => ({
    viewOption: 'all',
    showChangeDatesModal: false,
    timelineItems: new Map(),
    selectedTimelineAsset: null,
    isDragging: false,
    jumpToDate: null,
    campaignScenario: null,
    sidebarShowAssetDates: false,

    toggleSidebarShowAssetDates() {
      this.sidebarShowAssetDates = !this.sidebarShowAssetDates
    },

    get visibleActions() {
      const sid = scenarioIdRef.current
      return this.campaignScenario?.actions?.filter(action =>
        action.visible_in_budget &&
        (!action.campaign_scenario_id || !sid || +action.campaign_scenario_id === +sid)
      ) || []
    },

    setCampaignScenario(scenario) {
      this.campaignScenario = scenario
    },

    setJumpToDate(asset) {
      this.jumpToDate = asset.start_date;
    },

    clearJumpToDate() {
      this.jumpToDate = null
    },
    toggleChangeDatesModal(asset = null) {
      this.showChangeDatesModal = !this.showChangeDatesModal
      this.selectedTimelineAsset = asset
    },

    getItemsForDate(date) {
      const items = this.timelineItems.get(date) || [];

      const visibleItems = items.filter(item => {
        if (!(item instanceof CampaignScenariosProjectAsset)) {
          return item.visible_in_budget
        } else {
          return true
        }
      })

      switch (this.viewOption) {
        case 'project':
          return visibleItems.filter(item => item instanceof CampaignScenariosProjectAsset)
        case 'scenario':
          return visibleItems.filter(item => !(item instanceof CampaignScenariosProjectAsset))
        default: // 'all'
          return visibleItems
      }
    },

    getDaysBetween(startDate, endDate) {
      if (!startDate || !endDate) return 0

      try {
        const start = normalizeTimelineDate(startDate)
        const end = normalizeTimelineDate(endDate)
        // Check if dates are valid
        if (!start || !end) {
          return 0
        }
        return Math.max(0, differenceInCalendarDays(end, start))
      } catch (error) {
        console.error('Error calculating days between dates:', error)
        return 0
      }
    },

    addProjectAssetToTimeline(projectAssets, campaignScenario, assetId, date, endDate = null, notes = undefined) {
      if (!campaignIdRef.current || !scenarioIdRef.current) {
        toastError('Timeline is still loading. Please try again in a moment.')
        return Promise.resolve()
      }

      const asset = projectAssets.find(a => +a.id === +assetId)
      if (!asset) return Promise.resolve()

      // Create temporary asset for optimistic update
      const tempCspa = new CampaignScenariosProjectAsset({
        id: `temp-${Date.now()}`,
        project_asset_id: assetId,
        campaign_scenario_id: scenarioIdRef.current,
        start_date: date,
        end_date: endDate,
        ...(notes !== undefined ? { notes } : {})
      })

      // Add to timeline immediately for optimistic update
      const newItems = new Map(timelineState.timelineItems)
      const currentItems = newItems.get(date) || []
      newItems.set(date, [...currentItems, tempCspa])
      timelineState.timelineItems = newItems

      const createPayload = {
        project_asset_id: assetId,
        start_date: date
      }
      if (endDate != null && endDate !== '') {
        createPayload.end_date = endDate
      }
      if (notes !== undefined) {
        createPayload.notes = notes
      }

      return axiosService.post(
        `/api/project_manager/campaigns/${campaignIdRef.current}/campaign_scenarios/${scenarioIdRef.current}/campaign_scenarios_project_assets`,
        {
          campaign_scenarios_project_asset: createPayload
        }
      ).then(response => {
        // Create a new CampaignScenariosProjectAsset instance with the response data
        const cspa = new CampaignScenariosProjectAsset(response)

        // Update the timeline items with the new instance
        const updatedItems = new Map(timelineState.timelineItems)
        const dateItems = updatedItems.get(date) || []

        // Replace the temporary item with the new one
        const updatedDateItems = dateItems.map(item =>
          String(item.id) === String(tempCspa.id) ? cspa : item
        )

        updatedItems.set(date, updatedDateItems)
        timelineState.timelineItems = updatedItems

        // Also update the campaign scenario's assets
        if (campaignScenario) {
          campaignScenario.campaign_scenarios_project_assets = [
            ...campaignScenario.campaign_scenarios_project_assets.filter(a => a.id !== tempCspa.id),
            cspa
          ]
        }
      }).catch(error => {
        console.error('Error creating asset:', error);
        // Remove the temporary item on error
        const revertItems = new Map(timelineState.timelineItems)
        revertItems.set(date, (revertItems.get(date) || []).filter(item => item.id !== tempCspa.id))
        timelineState.timelineItems = revertItems
        toastError(error)
      })
    },

    addActionToTimeline(campaignScenario, actionId, date, providedEndDate = null, notes = undefined) {
      // Use the full actions array, not the filtered visibleActions
      const action = campaignScenario.actions.find(a => +a.id === +actionId)
      if (!action) {
        console.error(`Action with ID ${actionId} not found in campaign scenario`)
        toastError(`Action not found. Please refresh the page and try again.`)
        return Promise.resolve()
      }

      // Validate that the action belongs to the current campaign scenario
      if (action.campaign_scenario_id && scenarioIdRef.current && +action.campaign_scenario_id !== +scenarioIdRef.current) {
        console.error(`Action with ID ${actionId} belongs to campaign scenario ${action.campaign_scenario_id}, not ${scenarioIdRef.current}`)
        toastError('Action belongs to a different campaign scenario. Please refresh the page.')
        return Promise.resolve()
      }

      const shouldSetEndDate = !action.start_date

      const endDate = providedEndDate || 
        (shouldSetEndDate ? 
          new Date(new Date(date).setDate(new Date(date).getDate() + 3)).toISOString().split('T')[0] : // Add 3 days
          action.end_date)

      const tempAction = { 
        ...action, 
        start_date: date,
        end_date: endDate,
        ...(notes !== undefined ? { notes } : {})
      }

      // Add to timeline immediately for optimistic update
      const newItems = new Map(timelineState.timelineItems)
      const currentItems = newItems.get(date) || []
      newItems.set(date, [...currentItems, tempAction])
      timelineState.timelineItems = newItems

      const patchBody = {
        start_date: date,
        end_date: endDate
      }
      if (notes !== undefined) {
        patchBody.notes = notes
      }

      return axiosService.patch(
        `/api/project_manager/campaigns/${campaignIdRef.current}/campaign_scenarios/${campaignScenario.id}/actions/${actionId}`,
        {
          campaign_scenario_action: patchBody
        }
      ).then(response => {

        const updatedItems = new Map(timelineState.timelineItems)
        const dateItems = updatedItems.get(date) || []

        const updatedAction = {
          ...response,
          end_date: response.end_date || endDate
        }

        // Replace the temporary item with the updated one
        const updatedDateItems = dateItems.map(item => 
          String(item.id) === String(actionId) ? updatedAction : item
        )

        updatedItems.set(date, updatedDateItems)
        timelineState.timelineItems = updatedItems

        // Update the action in the campaign scenario with the complete data
        const actionIndex = campaignScenario.actions.findIndex(a => String(a.id) === String(actionId))
        if (actionIndex !== -1) {
          campaignScenario.actions[actionIndex] = { ...campaignScenario.actions[actionIndex], ...updatedAction }
        }
      }).catch(error => {
        // Remove the temporary item on error
        const revertItems = new Map(timelineState.timelineItems)
        revertItems.set(date, (revertItems.get(date) || []).filter(item => String(item.id) !== String(actionId)))
        timelineState.timelineItems = revertItems
        toastError(error.data?.error || 'Failed to update action')
        throw error
      })
    },

    updateTimelineItemDate(asset, oldDate, newDate, newEndDate = null, campaignScenario = null, notes = undefined) {
      // First, find the actual old date if it wasn't provided
      if (!oldDate) {
        this.timelineItems.forEach((items, date) => {
          if (items.some(item => String(item.id) === String(asset.id))) {
            oldDate = date;
          }
        });
      }

      // Find the item to move
      const oldDateItems = this.timelineItems.get(oldDate) || []
      const itemToMove = oldDateItems.find(item => String(item.id) === String(asset.id))

      if (!itemToMove) {
        return Promise.reject(new Error('Item not found'));
      }

      // Determine if the asset is a CampaignScenariosProjectAsset
      const isProjectAsset = asset instanceof CampaignScenariosProjectAsset

      // Choose the appropriate API endpoint and payload
      const endpoint = isProjectAsset
        ? `/api/project_manager/campaigns/${campaignIdRef.current}/campaign_scenarios/${asset.campaign_scenario_id}/campaign_scenarios_project_assets/${asset.id}`
        : `/api/project_manager/campaigns/${campaignIdRef.current}/campaign_scenarios/${scenarioIdRef.current}/actions/${asset.id}`

      const projectAssetPayload = {
        start_date: newDate,
        end_date: newEndDate || itemToMove.end_date
      }
      if (notes !== undefined) {
        projectAssetPayload.notes = notes
      }

      const actionPayload = {
        start_date: newDate,
        end_date: newEndDate || itemToMove.end_date
      }
      if (notes !== undefined) {
        actionPayload.notes = notes
      }

      const payload = isProjectAsset
        ? {
            campaign_scenarios_project_asset: projectAssetPayload
          }
        : {
            campaign_scenario_action: actionPayload
          }

      // Create optimistic update
      const updatedItem = {
        ...itemToMove,
        start_date: newDate,
        end_date: newEndDate || itemToMove.end_date,
        ...(notes !== undefined ? { notes } : {})
      }

      // Update the timeline items Map for optimistic update
      runInAction(() => {
        const newItems = new Map(this.timelineItems)

        // Remove from old date
        const remainingOldItems = (newItems.get(oldDate) || [])
          .filter(item => String(item.id) !== String(asset.id))

        if (remainingOldItems.length > 0) {
          newItems.set(oldDate, remainingOldItems)
        } else {
          newItems.delete(oldDate)
        }

        // Add to new date, preserving existing items
        const existingNewItems = newItems.get(newDate) || []
        newItems.set(newDate, [
          ...existingNewItems.filter(item => String(item.id) !== String(asset.id)),
          updatedItem
        ])

        this.timelineItems = newItems
      })

      return axiosService.patch(endpoint, payload)
        .then(response => {
          const serverUpdatedItem = isProjectAsset
            ? new CampaignScenariosProjectAsset(response)
            : new Action(response)

          runInAction(() => {
            const finalItems = new Map(this.timelineItems)

            // Update the item in the new date's array
            const currentItems = finalItems.get(newDate) || []
            finalItems.set(newDate, currentItems.map(item => 
              String(item.id) === String(asset.id) ? serverUpdatedItem : item
            ))

            this.timelineItems = finalItems

            // Update the asset in campaignScenario
            if (campaignScenario) {
              if (isProjectAsset) {
                const assetIndex = campaignScenario.campaign_scenarios_project_assets.findIndex(
                  item => String(item.id) === String(asset.id)
                )
                if (assetIndex !== -1) {
                  campaignScenario.campaign_scenarios_project_assets = [
                    ...campaignScenario.campaign_scenarios_project_assets.slice(0, assetIndex),
                    serverUpdatedItem,
                    ...campaignScenario.campaign_scenarios_project_assets.slice(assetIndex + 1)
                  ]
                }
              } else {
                const actionIndex = campaignScenario.actions.findIndex(
                  item => String(item.id) === String(asset.id)
                )
                if (actionIndex !== -1) {
                  campaignScenario.actions = [
                    ...campaignScenario.actions.slice(0, actionIndex),
                    serverUpdatedItem,
                    ...campaignScenario.actions.slice(actionIndex + 1)
                  ]
                }
              }
            }
          })
        })
    },

    loadScenarioAssets(scenarioId, campaignScenarioData) {
      if (!campaignScenarioData) return

      const newItems = new Map()

      // Load campaign scenario actions
      campaignScenarioData.actions?.forEach(action => {
        if (!action.start_date) return

        // Format the date consistently
        const date = getTimelineDateKey(action.start_date)
        if (!date) return
        const currentItems = newItems.get(date) || []
        newItems.set(date, [...currentItems, action])
      })

      // Load campaign scenario project assets
      campaignScenarioData.campaign_scenarios_project_assets?.forEach(asset => {
        if (!asset.start_date) return

        // Format the date consistently
        const date = getTimelineDateKey(asset.start_date)
        if (!date) return
        const currentItems = newItems.get(date) || []
        newItems.set(date, [...currentItems, new CampaignScenariosProjectAsset(asset)])
      })

      runInAction(() => {
        this.timelineItems = newItems
      })
    },

    deleteCampaignScenarioAction(actionId, date) {
      // Optimistically remove the item from the timeline
      const newItems = new Map(this.timelineItems)
      const currentItems = newItems.get(date) || []
      newItems.set(date, currentItems.filter(item => String(item.id) !== String(actionId)))
      this.timelineItems = newItems

      // Make the API call to delete the action
      axiosService.delete(
        `/api/project_manager/campaigns/${campaignIdRef.current}/campaign_scenarios/${campaignScenario.id}/campaign_scenario_actions/${actionId}`
      ).then(() => {
        // If successful, the item is already removed from the UI
      }).catch(error => {
        console.error('Error deleting campaign scenario action:', error)
        // Revert the deletion if there's an error
        const revertItems = new Map(this.timelineItems)
        const currentItems = revertItems.get(date) || []
        const deletedItem = this.campaignScenarioActions.find(csa => String(csa.id) === String(actionId))
        if (deletedItem) {
          revertItems.set(date, [...currentItems, deletedItem])
          this.timelineItems = revertItems
        }
        toastError('Failed to delete the action. Please try again.')
      })
    },

    isActionPlaced(actionId) {
      // Convert the Map values (arrays) into a single flat array of all items
      const allItems = Array.from(this.timelineItems.values()).flat()
      // Check if any item has matching action_id
      return allItems.some(item => String(item.action_id) === String(actionId))
    },

    isAssetUsedInTimeline(assetId) {
      const allItems = Array.from(this.timelineItems.values()).flat()
      return allItems.some(item => String(item.project_asset_id) === String(assetId))
    },

    setDragging(value) {
      this.isDragging = value;
    }
  }))

  useEffect(() => {
    if (readonly) {
      runInAction(() => {
        timelineState.viewOption = 'all'
      })
    }
  }, [readonly])

  const scenarioTimelineFingerprint =
    campaignScenario &&
    [
      campaignScenario.id,
      (campaignScenario.campaign_scenarios_project_assets || [])
        .map((a) => [a.id, a.start_date, a.end_date].join(':'))
        .join('|'),
      (campaignScenario.actions || [])
        .filter((a) => a.start_date)
        .map((a) => [a.id, a.start_date].join(':'))
        .join('|'),
    ].join('#')

  useEffect(() => {
    if (campaignScenario?.id) {
      timelineState.setCampaignScenario(campaignScenario)
      timelineState.loadScenarioAssets(campaignScenario.id, campaignScenario)
    }
  }, [campaignScenario?.id, scenarioTimelineFingerprint])

  return (
    <TimelineContext.Provider
      value={{
        timelineState: timelineState,
        jumpToDate: timelineState.jumpToDate,
        setJumpToDate: (asset) => timelineState.setJumpToDate(asset),
        clearJumpToDate: () => timelineState.clearJumpToDate(),
        viewOption: timelineState.viewOption,
        campaignScenario,
        actions: timelineState.visibleActions,
        timelineItems: timelineState.timelineItems,
        selectedTimelineAsset: timelineState.selectedTimelineAsset,
        showChangeDatesModal: timelineState.showChangeDatesModal,
        toggleChangeDatesModal: timelineState.toggleChangeDatesModal,
        isActionPlaced: timelineState.isActionPlaced,
        isDragging: timelineState.isDragging,
        setDragging: timelineState.setDragging,
        sidebarShowAssetDates: timelineState.sidebarShowAssetDates,
        toggleSidebarShowAssetDates: timelineState.toggleSidebarShowAssetDates,
        readonly,
        layout,
      }}
    >
      {children}
    </TimelineContext.Provider>
  )
})
